export const SKIP_ANCHOR_TYPES = new Set(["signature", "yousing", "esign"]);

export const SOURCE_MODES = {
  KEY: "key",
  KEYS: "keys",
  FROM: "from",
  COMBINE: "combine",
  STATIC: "static",
};

export const SOURCE_MODE_OPTIONS = [
  { value: SOURCE_MODES.KEY, key: "KEY" },
  { value: SOURCE_MODES.KEYS, key: "KEYS" },
  { value: SOURCE_MODES.FROM, key: "FROM" },
  { value: SOURCE_MODES.COMBINE, key: "COMBINE" },
];

export const PART_MODE_OPTIONS = [
  ...SOURCE_MODE_OPTIONS,
  { value: SOURCE_MODES.STATIC, key: "STATIC" },
];

export const TRANSFORMS = [
  {
    op: "date",
    key: "DATE",
    fields: [
      {
        name: "format",
        key: "FORMAT",
        type: "select",
        allowCustom: true,
        default: "DD/MM/YYYY",
        options: [
          { value: "DD/MM/YYYY", key: "SHORT" },
          { value: "D MMMM YYYY", key: "LONG" },
          { value: "D MMM YYYY", key: "LONG_ABBR" },
          { value: "dddd D MMMM YYYY", key: "FULL" },
          { value: "MM/YYYY", key: "MONTH_YEAR" },
          { value: "YYYY-MM-DD", key: "ISO" },
          { value: "DDMMYYYY", key: "COMPACT" },
          { value: "HH:mm", key: "TIME" },
          { value: "DD/MM/YYYY HH:mm", key: "DATE_TIME" },
        ],
      },
    ],
  },
  {
    op: "dateShift",
    key: "DATE_SHIFT",
    fields: [
      { name: "amount", key: "AMOUNT", type: "text" },
      {
        name: "unit",
        key: "UNIT",
        type: "select",
        default: "day",
        options: [
          { value: "day", key: "DAY" },
          { value: "week", key: "WEEK" },
          { value: "month", key: "MONTH" },
          { value: "year", key: "YEAR" },
        ],
      },
      { name: "table", key: "TABLE", type: "keyValue" },
    ],
  },
  {
    op: "map",
    key: "MAP",
    fields: [
      { name: "table", key: "TABLE", type: "keyValue" },
      { name: "default", key: "DEFAULT", type: "text" },
    ],
  },
  {
    op: "bool",
    key: "BOOL",
    fields: [
      { name: "true", key: "TRUE", type: "text", default: "Oui" },
      { name: "false", key: "FALSE", type: "text", default: "Non" },
      { name: "empty", key: "EMPTY", type: "text" },
    ],
  },
  {
    op: "case",
    key: "CASE",
    fields: [
      {
        name: "mode",
        key: "MODE",
        type: "select",
        default: "capitalize",
        options: [
          { value: "capitalize", key: "CAPITALIZE" },
          { value: "upper", key: "UPPER" },
          { value: "lower", key: "LOWER" },
          { value: "title", key: "TITLE" },
        ],
      },
    ],
  },
  {
    op: "number",
    key: "NUMBER",
    fields: [
      { name: "decimals", key: "DECIMALS", type: "number", default: 2 },
      {
        name: "decimalSeparator",
        key: "DECIMAL_SEPARATOR",
        type: "text",
        default: ",",
      },
      {
        name: "thousandsSeparator",
        key: "THOUSANDS_SEPARATOR",
        type: "text",
      },
    ],
  },
  {
    op: "phone",
    key: "PHONE",
    fields: [
      { name: "groupSize", key: "GROUP_SIZE", type: "number", default: 2 },
      { name: "separator", key: "SEPARATOR", type: "text", default: " " },
      { name: "expectLength", key: "EXPECT_LENGTH", type: "number" },
    ],
  },
  {
    op: "replace",
    key: "REPLACE",
    fields: [
      { name: "search", key: "SEARCH", type: "text" },
      { name: "replace", key: "REPLACE", type: "text" },
      { name: "regex", key: "REGEX", type: "boolean" },
    ],
  },
  { op: "trim", key: "TRIM", fields: [] },
  { op: "normalizeSpaces", key: "NORMALIZE_SPACES", fields: [] },
  { op: "stripLeadingZeros", key: "STRIP_LEADING_ZEROS", fields: [] },
  {
    op: "pad",
    key: "PAD",
    fields: [
      { name: "length", key: "LENGTH", type: "number" },
      { name: "char", key: "CHAR", type: "text", default: "0" },
      {
        name: "side",
        key: "SIDE",
        type: "select",
        default: "start",
        options: [
          { value: "start", key: "START" },
          { value: "end", key: "END" },
        ],
      },
    ],
  },
  {
    op: "slice",
    key: "SLICE",
    fields: [
      { name: "start", key: "START", type: "number" },
      { name: "end", key: "END", type: "number" },
    ],
  },
  {
    op: "prefix",
    key: "PREFIX",
    fields: [{ name: "value", key: "VALUE", type: "text" }],
  },
  {
    op: "suffix",
    key: "SUFFIX",
    fields: [{ name: "value", key: "VALUE", type: "text" }],
  },
  {
    op: "default",
    key: "DEFAULT",
    fields: [{ name: "value", key: "VALUE", type: "text" }],
  },
  {
    op: "join",
    key: "JOIN",
    fields: [
      { name: "separator", key: "SEPARATOR", type: "text", default: ", " },
    ],
  },
  {
    op: "split",
    key: "SPLIT",
    fields: [{ name: "separator", key: "SEPARATOR", type: "text" }],
  },
  {
    op: "sum",
    key: "SUM",
    fields: [{ name: "decimals", key: "DECIMALS", type: "number" }],
  },
  { op: "count", key: "COUNT", fields: [] },
  {
    op: "pluck",
    key: "PLUCK",
    fields: [{ name: "key", key: "KEY", type: "text" }],
  },
  { op: "unique", key: "UNIQUE", fields: [] },
  { op: "first", key: "FIRST", fields: [] },
  { op: "last", key: "LAST", fields: [] },
];

export const TRANSFORM_BY_OP = Object.fromEntries(
  TRANSFORMS.map((transform) => [transform.op, transform]),
);

export const PDF_CONTENT_TYPE = "application/pdf";

export const MAPPING_TYPES = {
  STATIC: "static",
  SPREAD: "spread",
  DENTAL: "dental",
  CHILD_LOOP: "childLoop",
};

export const CONDITION_OPERATORS = {
  AND: "AND",
  OR: "OR",
};

export const RULE_OPERATORS = {
  EQUALS: "equals",
  NOT_EQUALS: "not_equals",
  CONTAINS: "contains",
  NOT_CONTAINS: "not_contains",
};

export const EMPTY_RULE = { key: "", op: RULE_OPERATORS.EQUALS, value: "" };

export const EMPTY_CONDITIONS = {
  operator: CONDITION_OPERATORS.AND,
  rules: [],
};
